import { Observable } from "rxjs";
import { combineEpics } from "redux-observable";

import st from "lib/utils/index";
import { CustomerDecorator } from "lib/decorators/index";
import { CustomersRepository } from "lib/repositories/index";
import { replaceCompanyRoute } from "modules/app/actions";
import { createResourceInstance } from "modules/utils/index";
import createFilteredItemsInstance, {
  filterEqual,
  filterDateRange,
  filterIncludes,
  filterQuery,
  aggregateBy,
  aggregateByBooleans,
} from "modules/utils/createFilteredItemsInstance";

export const INITIALIZE_VIEW = "customers/CustomersListView/INITIALIZE_VIEW";
export const DESTROY_VIEW = "customers/CustomersListView/DESTROY_VIEW";
export const LOAD_RECORDS = "customers/CustomersListView/LOAD_RECORDS";
export const FETCH_RECORDS = "customers/CustomersListView/FETCH_RECORDS";
export const FETCH_RECORDS_START = "customers/CustomersListView/FETCH_RECORDS_START";
export const FETCH_RECORDS_SUCCESS = "customers/CustomersListView/FETCH_RECORDS_SUCCESS";
export const FETCH_RECORDS_FAILURE = "customers/CustomersListView/FETCH_RECORDS_FAILURE";
export const SET_FILTERS = "customers/CustomersListView/SET_FILTERS";
export const REFILTER = "customers/CustomersListView/REFILTER";
export const CHANGE_PAGINATION = "orders/CustomersListView/CHANGE_PAGINATION";
export const SET_PAGINATION = "customers/CustomersListView/SET_PAGINATION";
export const REPAGINATE = "customers/CustomersListView/REPAGINATE";

export const stateSelector = (state) => state.customers.CustomersListView;

const recordsResource = createResourceInstance({
  LOAD: LOAD_RECORDS,
  FETCH: FETCH_RECORDS,
  FETCH_START: FETCH_RECORDS_START,
  FETCH_SUCCESS: FETCH_RECORDS_SUCCESS,
  FETCH_FAILURE: FETCH_RECORDS_FAILURE,
  stateSelector,
  stateKey: "allRecords",
  getModel: ({ offset, limit }) => {
    const page = offset / limit + 1;
    return CustomersRepository.findAll({ page, per: limit });
  },
});

const filtersInstance = createFilteredItemsInstance({
  SET_FILTERS,
  REFILTER,
  stateSelector,
  allItemsStateSelector: (state) => state.allRecords.model || [],
  filteredItemsStateKey: "filteredRecords",
  initialFilters: {
    status: null,
    author_place: null,
    created_at: null,
    query: null,
  },
  filterTypes: {
    status: "String[]",
    author_place: "String[]",
    created_at: "Date",
    query: "String",
  },
  filterFn: (allRecords, filters) => {
    return allRecords.records.filter((c) => {
      return (
        true &&
        (!filters.status ||
          false ||
          (filters.status.includes("prospect") && filterEqual(c.prospect, true)) ||
          (filters.status.includes("future") && filterEqual(c.future, true)) ||
          (filters.status.includes("past") && filterEqual(c.past, true)) ||
          (filters.status.includes("lost") && filterEqual(c.lost, true))) &&
        filterIncludes(c.author_place, filters.author_place) &&
        filterDateRange(c.created_at, filters.created_at) &&
        (filters.query === null ||
          filterQuery(c.full_name, filters.query) ||
          filterQuery(c.email, filters.query) ||
          filterQuery(c.phone_number, filters.query))
      );
    });
  },
  initialAggregations: {
    status: {},
    author_place: {},
  },
  aggregateFn: (allRecords, filters, filterFn) => {
    return {
      status: aggregateByBooleans(allRecords, filters, filterFn, "status", CustomerDecorator.statuses),
      author_place: aggregateBy(allRecords, filters, filterFn, "author_place"),
    };
  },
});

const limit = parseInt(localStorage.getItem("clientsLimit"), 10) || 10;
const initialState = {
  allRecords: recordsResource.initialState,
  ...filtersInstance.initialState,
  currentPageRecords: [],
  offset: 0,
  limit: limit,
};

const reducers = {
  ...recordsResource.reducersMap,
  ...filtersInstance.reducersMap,

  [INITIALIZE_VIEW]: () => initialState,

  [SET_PAGINATION]: (state, { offset = state.offset, limit = state.limit }) => ({
    ...state,
    offset,
    limit,
  }),

  [REPAGINATE]: (state) => {
    return {
      ...state,
      currentPageRecords: state.filteredRecords.slice(state.offset, state.offset + state.limit),
    };
  },
};

const viewEpics = [
  ...recordsResource.epics,

  (actions$) => actions$.ofType(CHANGE_PAGINATION).map(({ offset, limit }) => setPagination({ offset, limit })),

  (actions$) => actions$.ofType(CHANGE_PAGINATION).debounceTime(300).mapTo(fetchRecords()),

  (actions$) =>
    Observable.of(null)
      .merge(actions$.ofType(SET_FILTERS).debounceTime(150))
      .combineLatest(actions$.ofType(FETCH_RECORDS_SUCCESS).merge(actions$.ofType(LOAD_RECORDS)))
      .mapTo({ type: REFILTER }),

  (actions$, { getState }) =>
    actions$
      .ofType(SET_FILTERS)
      .debounceTime(500)
      .map(() => {
        const { filters } = stateSelector(getState());
        const query = filtersInstance._serializeFiltersToLocationQuery(filters);
        return replaceCompanyRoute({ pathname: "/customers", query });
      }),
];

const epics = [
  (actions$, store) =>
    actions$
      .ofType(INITIALIZE_VIEW)
      .concatMap(() => combineEpics(...viewEpics)(actions$, store).takeUntil(actions$.ofType(DESTROY_VIEW))),
];

export const { setFilters, setFiltersWithLocationQuery } = filtersInstance.actions;
export const { load: loadRecords, fetch: fetchRecords } = recordsResource.actions;

export function initializeView() {
  return { type: INITIALIZE_VIEW };
}

export function destroyView() {
  return { type: DESTROY_VIEW };
}

export function changePagination({ offset, limit }) {
  return { type: CHANGE_PAGINATION, offset, limit };
}

export function setPagination({ offset, limit }) {
  return { type: SET_PAGINATION, offset, limit };
}

export function setParamsWithLocationQuery({ o = initialState.offset, l = initialState.limit, ...query } = {}) {
  return (dispatch) => {
    dispatch(setFiltersWithLocationQuery(query));
    dispatch(setPagination({ offset: +o, limit: +l }));
    dispatch(fetchRecords());
  };
}

export default st.duck.createDuck(initialState, reducers, epics);



// WEBPACK FOOTER //
// ./src/modules/customers/CustomersListView/index.js